/**
 * PotreeSparkAdapter — convenience API around @sparkjsdev/spark v2.1.0 for use
 * from Potree viewers.
 *
 * The main entry point is `loadGaussianSplat(viewer, source, options)`, which
 * lazily loads spark, creates a single SparkRenderer per viewer, instantiates
 * a `SplatMesh`, wires up depth-test defaults so the splat coexists with the
 * point cloud, and attaches it to `viewer.scene.sceneGaussianSplat` via the
 * existing Potree API `viewer.scene.addGaussianSplats(mesh)`.
 *
 * Supported source formats (via spark v2.1.0):
 *   .ply     regular 3DGS ply
 *   .spz     spark compressed
 *   .splat   raw splat
 *   .ksplat  compact splat
 *   .rad     RADiance field (streaming, v2.0.0+)
 *   .zip     PCSOGS v1/v2 bundle
 *   .ply     also supports plain xyz/rgb point clouds since v2.1.0 (#340)
 */
import * as THREE from "../../../libs/three.js/build/three.module.js";
import { load as loadSparkModule } from "./SparkLoader.js";
import { install as installSplatClipBridge } from "./SplatClipBridge.js";

/**
 * Ensure a single `SparkRenderer` is attached to `viewer.scene.sceneGaussianSplat`.
 * Multiple `SplatMesh` instances share the same renderer — this matches spark's
 * v2.0.0 breaking-change convention where SparkRenderer must be created explicitly.
 *
 * The renderer is cached on `viewer.__sparkRenderer` so subsequent calls are cheap.
 */
async function ensureSparkRenderer(viewer, options = {}) {
	// 缓存命中还需校验渲染器仍在场景内：removeAllGaussianSplats 会把子对象
	// （含 SparkRenderer）摘走并 dispose，但缓存未失效，若直接复用会拿到一个
	// 已脱离场景的渲染器，跨实例/原生 mesh 都将失去收集者。
	if (viewer.__sparkRenderer && viewer.__sparkRenderer.parent === viewer.scene.sceneGaussianSplat) {
		return viewer.__sparkRenderer;
	}
	// 并发守卫：模块加载期间缓存尚未写入，连续触发（如多 mesh 连发 addGaussianSplats）
	// 会对同一 viewer 并发创建多个 SparkRenderer，这里缓存 in-flight promise 合并。
	if (viewer.__sparkRendererPending) return viewer.__sparkRendererPending;
	viewer.__sparkRendererPending = _createSparkRenderer(viewer, options);
	try {
		return await viewer.__sparkRendererPending;
	} finally {
		delete viewer.__sparkRendererPending;
	}
}

async function _createSparkRenderer(viewer, options = {}) {
	const spark = await loadSparkModule();
	const { SparkRenderer } = spark;

	// 高精度坐标(fp32)对**分页** splat(.rad 流式 / paged:true / 部分 LOD)必须
	// 通过 SparkRenderer.pagedExtSplats 开启 -- 分页路径会忽略 SplatMesh 的
	// extSplats(spark 源码 SplatMesh 构造里对 paged+extSplats 直接 console.warn
	// 并走分页分支)。这里用同一个 viewer.__gsExtSplats 开关同时驱动两条路径。
	const pagedExt = options.pagedExtSplats ?? viewer.__gsExtSplats ?? false;

	const sparkRenderer = new SparkRenderer({
		premultipliedAlpha: options.premultipliedAlpha ?? true,
		renderer: viewer.renderer,
		pagedExtSplats: pagedExt,
	});
	viewer.scene.sceneGaussianSplat.add(sparkRenderer);
	viewer.__sparkRenderer = sparkRenderer;
	// 缓存 spark 模块命名空间，供 disposeSparkRenderer reach-in 回收 compute 材质用
	// (SplatAccumulator.generatorProgram 是公开静态 WeakMap；DynoProgram.prepareMaterial
	// 是公开方法)。不加这一份，dispose 时无法从 generator 反查到 RawShaderMaterial。
	viewer.__sparkModule = spark;
	return sparkRenderer;
}

/**
 * Load and mount a 3DGS scene onto the viewer.
 *
 * @param {object} viewer - a Potree.Viewer instance
 * @param {string|{fileBytes: Uint8Array, fileName: string}} source
 *   - string: URL of a splat file (.ply / .spz / .splat / .ksplat / .rad / .zip)
 *   - object: { fileBytes, fileName } for in-memory files (file input, drag&drop)
 * @param {object} [options]
 * @param {boolean} [options.lod=false]         enable spark v2.0.0 LOD streaming
 * @param {boolean} [options.premultipliedAlpha=true]
 * @param {boolean} [options.depthTest=true]    keep depth-test on (recommended when
 *                                              coexisting with point clouds)
 * @param {boolean} [options.depthWrite=true]
 * @param {number}  [options.depthFunc=THREE.LessEqualDepth]
 * @param {string}  [options.name]              assigned to mesh.name (for lookup / removal)
 * @param {(mesh)=>void} [options.onLoad]       callback invoked after the mesh finished loading
 * @param {(err)=>void}  [options.onError]      callback invoked on load failure
 * @returns {Promise<any>} resolves with the spark SplatMesh instance
 */
export async function loadGaussianSplat(viewer, source, options = {}) {
	const spark = await loadSparkModule();
	const { SplatMesh } = spark;

	await ensureSparkRenderer(viewer, options);

	const depthTest  = options.depthTest  ?? true;
	const depthWrite = options.depthWrite ?? true;
	const depthFunc  = options.depthFunc  ?? THREE.LessEqualDepth;

	const init = typeof source === "string"
		? { url: source }
		: (source && source.fileBytes ? { fileBytes: source.fileBytes, fileName: source.fileName } : source);

	// 记录"重载所需的 source"。extSplats 是构造期选项，切换时必须重新 new
	// SplatMesh，而 spark 不在 mesh 上保留 url、且会把内存文件的 ArrayBuffer
	// transfer 到 worker 并 detach，所以内存文件必须自持一份 slice 副本。
	const trackSource = _snapshotSource(source);

	// spark v2.0.0+ 的 .rad 是分块流式（index + .radc chunks），必须走 PagedSplats
	// 路径。SplatMesh 不会自动嗅探 URL 扩展，不显式设置就走普通 SplatLoader，
	// 会抛 `Worker error: Incomplete RAD chunk`。这里根据 URL / fileName 扩展识别。
	const looksLikeRad =
		(typeof source === "string" && /\.rad(\?|$|#)/i.test(source)) ||
		(source && typeof source.fileName === "string" && /\.rad$/i.test(source.fileName)) ||
		options.fileType === "rad" || options.fileType === "RAD";
	if (looksLikeRad && init.paged === undefined) {
		init.paged = true;
	}

	if (options.lod === true) {
		init.lod = true;
	}

	// 存储精度决策：显式 options.extSplats 优先，其次 viewer 级开关（侧栏
	// "高精度坐标 fp32"），默认 false（fp16 PackedSplats，更省显存）。
	// - 非分页 mesh：extSplats:true → SplatMesh 用 fp32 存中心（ExtSplats）。
	// - 分页 mesh（.rad / paged）：extSplats 被 spark 忽略（会 console.warn），
	//   高精度改由 SparkRenderer.pagedExtSplats 负责（见 ensureSparkRenderer），
	//   所以这里对分页 mesh 不设 init.extSplats，避免刷警告。
	// 注意：必须在 init.paged 确定之后再判断。
	const useExt = options.extSplats ?? viewer.__gsExtSplats ?? false;
	if (useExt && !init.paged) {
		init.extSplats = true;
	}

	return new Promise((resolve, reject) => {
		const gs = new SplatMesh({
			...init,
			onLoad: (mesh) => {
				try {
					// 材质/命名等无依赖动作立即执行（不依赖分页数据）。
					mesh.traverse((child) => {
						if (child.material) {
							child.material.depthWrite = depthWrite;
							child.material.depthTest  = depthTest;
							child.material.depthFunc  = depthFunc;
						}
					});
					if (options.name) mesh.name = options.name;

					// paged 路径（.rad / paged:true）：spark 的 SplatMesh.asyncInitialize
					// 不 await 分块流式，onLoad 在 numSplats=0、包围盒为空时就 fire。
					// 若立即把 onLoad 暴露给调用方（典型用法：onLoad 里 fitToScreen），
					// 对空包围盒会把相机推到无效位置 -> 黑屏。
					// 延迟到 mesh.numSplats>0（每帧由 SplatMesh.update 从
					// PagedSplats.getNumSplats() 同步，仅反映分页数据到位，不耦合相机
					// 可见性）后再触发；10s 超时兜底后照常触发，避免阻塞加载流程。
					if (init.paged) {
						let waited = 0;
						const tick = () => {
							if (mesh.numSplats > 0 || waited >= 10000) {
								options.onLoad?.(mesh);
								resolve(mesh);
							} else {
								waited += 16;
								requestAnimationFrame(tick);
							}
						};
						requestAnimationFrame(tick);
					} else {
						options.onLoad?.(mesh);
						resolve(mesh);
					}
				} catch (e) {
					options.onError?.(e);
					reject(e);
				}
			},
		});

		// SplatMesh construction can synchronously fail before onLoad fires (e.g. bad URL).
		// spark surfaces errors via its own event; wire a basic timeout-free reject fallback.
		gs.addEventListener?.("error", (e) => {
			options.onError?.(e);
			reject(e);
		});

			viewer.scene.addGaussianSplats(gs);
		}).then((mesh) => {
			// Stash everything needed to rebuild this splat with a different
			// construction-time option (e.g. extSplats fp16<->fp32). The mesh
			// itself is the registry: reloadGaussianSplats reads it off the
			// currently-loaded meshes, so it always reflects the live scene.
			mesh.__sparkSource = trackSource;
			mesh.__sparkReloadOptions = {
				name: options.name,
				lod: options.lod,
				paged: init.paged,
				depthTest,
				depthWrite,
				depthFunc,
				premultipliedAlpha: options.premultipliedAlpha,
			};

			// Install / refresh the clip bridge after each splat lands.
			// Mirrors viewer.clipTask / scene.volumes[clip=true] to spark's SplatEdit
			// so 3DGS scenes respect Potree's Clipping sidebar. install() is
			// idempotent (no-op when the bridge is still live) and self-heals when
			// the prior editBox was disposed by removeAllGaussianSplats - so it is
			// safe to call every load rather than gating on a once-only flag. The
			// once-only flag approach broke re-load: callers that clear splats
			// (demo pages, manual removeAllGaussianSplats) often forget to drop
			// _splatClipInstalled, so install was skipped and the stale, disposed
			// bridge was reused -> clip/highlight (red tint) silently died.
			try {
				installSplatClipBridge(viewer, spark);
				viewer._splatClipInstalled = true;
			} catch (e) {
				console.warn("[3DGS] SplatClipBridge install failed:", e);
			}
			return mesh;
		});
}

/**
 * Snapshot a source so it can be re-loaded later. URLs are reusable as-is;
 * in-memory files must keep their own copy because spark transfers (and thus
 * detaches) the ArrayBuffer to its decode worker.
 */
function _snapshotSource(source) {
	if (typeof source === "string") return source;
	if (source && source.fileBytes) {
		const bytes = source.fileBytes instanceof Uint8Array
			? source.fileBytes
			: new Uint8Array(source.fileBytes);
		return { fileBytes: bytes.slice(0), fileName: source.fileName };
	}
	return null;
}

/**
 * Produce a fresh, load-ready copy of a snapshotted source. In-memory bytes
 * are re-sliced each time so repeated reloads keep working after each transfer.
 */
function _cloneSource(snapshot) {
	if (typeof snapshot === "string") return snapshot;
	if (snapshot && snapshot.fileBytes) {
		return { fileBytes: snapshot.fileBytes.slice(0), fileName: snapshot.fileName };
	}
	return null;
}

/**
 * Rebuild all currently-loaded 3DGS meshes, optionally overriding
 * construction-time options (notably `extSplats`). Needed because storage
 * precision (fp16 PackedSplats vs fp32 ExtSplats) is fixed at construction.
 *
 * Reads the source snapshots off the live `scene.gaussianSplats`, tears down
 * the whole spark sub-scene (splats + shared SparkRenderer + clip bridge — all
 * disposed by removeAllGaussianSplats), then re-loads each source.
 *
 * @param {object} viewer
 * @param {object} [override] - e.g. { extSplats: true }
 * @returns {Promise<any[]>} the reloaded SplatMesh instances
 */
export async function reloadGaussianSplats(viewer, override = {}) {
	const metas = (viewer.scene.gaussianSplats || [])
		.map((m) => ({ source: m && m.__sparkSource, options: (m && m.__sparkReloadOptions) || {} }))
		.filter((x) => x.source != null);

	if (override.extSplats !== undefined) {
		viewer.__gsExtSplats = override.extSplats;
	}

	// Nothing loaded (or nothing reloadable): the flag is set for future loads.
	if (metas.length === 0) {
		if ((viewer.scene.gaussianSplats || []).length > 0) {
			console.warn("[3DGS] reload skipped: loaded splat(s) have no tracked source");
		}
		return [];
	}

	// 彻底释放当前 spark 子场景（splats + 共享 SparkRenderer + clip bridge，含
	// SparkRenderer 泄漏的 geometry）并清掉 viewer 级缓存，下一次 loadGaussianSplat
	// 会重建全新的 renderer + bridge。
	disposeSparkRenderer(viewer);

	const reloaded = [];
	for (const meta of metas) {
		const mesh = await loadGaussianSplat(viewer, _cloneSource(meta.source), {
			...meta.options,
			extSplats: viewer.__gsExtSplats,
		});
		reloaded.push(mesh);
	}
	return reloaded;
}

/**
 * Reach-in 回收 spark 在 Potree 渲染器里"注册过却没人 dispose"的材质。
 *
 * 背景：spark 把 Potree 的 WebGLRenderer 传进去跑 compute/accumulate pass，
 * 生成的 RawShaderMaterial 会被 Potree 的 WebGLShaderCache.materialCache 强引用
 * （按材质做 key 的 Map，仅在 material.dispose() 时驱逐）。但 spark 自己的
 * dispose() 从不对这些材质调 dispose()，于是每加载一个新 SplatMesh（= 新
 * generator = 新 compute 材质，uniform 强引用 ~1.56MB 源纹理）都永久泄漏。
 *
 * 这里在 dispose 之前，沿 spark 公开结构反查到这些材质并显式 dispose()，
 * 让 Potree 渲染器把它们从 materialCache 驱逐：
 *   mesh.generator | mesh.covGenerator
 *     -> SplatAccumulator.generatorProgram.get(generator)   (公开静态 WeakMap)
 *     -> DynoProgram.prepareMaterial()                      (公开方法，返回缓存材质)
 *     -> material.dispose()                                 (触发 WebGLShaderCache 驱逐)
 *
 * 同时回收 SparkRenderer 自身的 display 材质（SparkRenderer.dispose 也不释放它）。
 * 所有访问都用可选链兜底，依赖的内部结构若在新版 spark 变化也不会崩。
 */
function _disposeSparkRetainedMaterials(viewer) {
	const spark = viewer.__sparkModule;
	const renderer = viewer.__sparkRenderer;

	// 诊断日志：排查 reach-in 是否真的拿到并 dispose 了 compute 材质
	const diag = { meshCount: 0, withGen: 0, programHit: 0, materialHit: 0, disposed: 0, errors: [] };

	// 1) 每个 SplatMesh 的 compute 材质（主项：+1.56MB 源纹理/轮 的来源）
	if (spark?.SplatAccumulator?.generatorProgram) {
		const generatorProgram = spark.SplatAccumulator.generatorProgram;
		const meshes = viewer.scene?.gaussianSplats || [];
		diag.meshCount = meshes.length;
		for (const mesh of meshes) {
			if (!mesh) continue;
			// SplatMesh 最多一个 generator（gsplat 或 cov，互斥）
			const generators = [mesh.generator, mesh.covGenerator];
			let meshHasGen = false;
			for (const generator of generators) {
				if (!generator) continue;
				meshHasGen = true;
				try {
					const program = generatorProgram.get(generator);
					if (program) diag.programHit++;
					// prepareMaterial() 返回缓存的 RawShaderMaterial（不新建）
					const material = program?.prepareMaterial?.();
					if (material) diag.materialHit++;
					// 验证 material.dispose 是否真触发了 Potree 渲染器清理：
					// 检查材质上有没有 dispose 事件监听（跨 THREE 实例时可能没注册上）
					const hasDisposeListener = material && typeof material.hasEventListener === "function"
						? material.hasEventListener("dispose") : "(no hasEventListener)";
					diag.lastHasListener = hasDisposeListener;
					material?.dispose?.();
					diag.disposed++;
				} catch (e) { diag.errors.push(e && e.message || String(e)); }
			}
			if (meshHasGen) diag.withGen++;
		}
	} else {
		diag.noSparkModule = !spark;
		diag.noGeneratorProgram = spark && !spark.SplatAccumulator?.generatorProgram;
	}

	// 2) SparkRenderer 自身的 display 材质（SparkRenderer.dispose 不释放它）
	const dispMat = renderer?.material;
	const dispHasListener = dispMat && typeof dispMat.hasEventListener === "function"
		? dispMat.hasEventListener("dispose") : "(no hasEventListener)";
	diag.displayHasListener = dispHasListener;
	try { dispMat?.dispose?.(); } catch (e) { /* noop */ }

	console.log("[3DGS] _disposeSparkRetainedMaterials diag:", diag);
}

/**
 * Fully tear down all 3DGS resources bound to a viewer - the canonical
 * "release everything" entry point for business / demo code (exposed as
 * `Potree.disposeSparkRenderer(viewer)`).
 *
 * Handles the three leak-prone pieces in one call:
 *   1. Every SplatMesh / SplatEdit child AND the shared SparkRenderer are
 *      disposed & removed via `scene.removeAllGaussianSplats()`. That method
 *      now duck-types geometry disposal, so the SparkRenderer's SplatGeometry -
 *      which spark's own `SparkRenderer.dispose()` never frees - is released too;
 *      otherwise each load/dispose cycle leaks exactly 1 geometry.
 *   2. The clip/highlight bridge (SplatClipBridge) is disposed.
 *   3. All viewer-level caches (`__sparkRenderer` / `_splatClipBridge` /
 *      `_splatClipInstalled`) are dropped so the next `loadGaussianSplat`
 *      rebuilds a fresh renderer + bridge instead of reusing disposed ones.
 *
 * Idempotent and safe to call when nothing is loaded.
 *
 * @param {object} viewer - a Potree.Viewer instance
 */
export function disposeSparkRenderer(viewer) {
	if (!viewer || !viewer.scene) return;

	// 0. dispose 之前先 reach-in 回收 spark 注册到 Potree 渲染器、但 spark 自己
	//    dispose 不释放的材质（compute 材质 + display 材质）。必须在 removeAllGaussianSplats
	//    / SparkRenderer.dispose 之前做：那时 mesh.generator 还在、SparkRenderer 引用还在，
	//    能反查到材质；之后 SplatMesh 被 dispose，generator 可能被置空就够不到了。
	//    关键：material.dispose() 触发 Potree 渲染器把材质从 WebGLShaderCache.materialCache
	//    驱逐，连带释放被 uniform 强引用的 ~1.56MB 源纹理 —— 这是融合泄漏的主要来源。
	try {
		_disposeSparkRetainedMaterials(viewer);
	} catch (e) {
		console.warn("[3DGS] disposeSparkRetainedMaterials failed:", e);
	}

	// 1. dispose + remove 全部高斯子对象（含 SparkRenderer 及其泄漏的 geometry）。
	try {
		viewer.scene.removeAllGaussianSplats();
	} catch (e) {
		console.warn("[3DGS] removeAllGaussianSplats failed:", e);
	}

	// 2. 双保险：若 SparkRenderer 因故没被上面的 children 循环覆盖（例如被从
	//    sceneGaussianSplat 手动移走但 viewer 缓存仍在），仍显式补齐 dispose +
	//    geometry.dispose（spark 的 dispose 不释放这个 geometry）。
	//    display 材质已在第 0 步 reach-in 释放过，这里再 dispose 一次也无害（幂等）。
	const r = viewer.__sparkRenderer;
	if (r) {
		try { r.dispose?.(); } catch (e) { /* noop */ }
		try { r.material?.dispose?.(); } catch (e) { /* noop */ }
		try { r.geometry?.dispose?.(); } catch (e) { /* noop */ }
		try { viewer.scene.sceneGaussianSplat?.remove(r); } catch (e) { /* noop */ }
	}

	// 3. dispose 裁剪/高亮桥接，丢弃所有 viewer 级缓存。
	try { viewer._splatClipBridge?.dispose?.(); } catch (e) { /* noop */ }
	delete viewer.__sparkRenderer;
	delete viewer.__sparkModule;
	delete viewer.__sparkRendererPending;
	delete viewer._splatClipBridge;
	delete viewer._splatClipInstalled;
}

/**
 * Get the raw spark module namespace (advanced users). Useful for constructing
 * `SparkXr` for AR/VR, `PagedSplats`, `SplatEdit`, custom `dyno` shader graphs, etc.
 *
 * @returns {Promise<any>} the full spark v2.1.0 module namespace
 */
export async function getSpark() {
	return await loadSparkModule();
}

/**
 * Access the SparkRenderer bound to a viewer (creates one on first call).
 * Useful when the caller wants to tweak renderer options directly
 * (e.g. `lodSplatCount`, `focalAdjustment`, `stochastic`).
 *
 * @param {object} viewer
 * @param {object} [options]
 * @returns {Promise<any>} the shared SparkRenderer
 */
export async function getSparkRenderer(viewer, options) {
	return ensureSparkRenderer(viewer, options);
}
